/* ==========================================================================
   SPECGT header & commerce navigation (Phase 1 — visual correction)
   --------------------------------------------------------------------------
   Vanilla JS. No library, no dependencies, unminified.

   Principles:
   - Progressive enhancement. Every navigation link is server-rendered Liquid
     and works with JavaScript disabled; this file only adds disclosure
     behaviour, focus management and ARIA state.
   - No scroll listener anywhere. The top/scrolled state comes from a single
     IntersectionObserver watching a 1px sentinel.
   - One AbortController per instance, so destroy() removes every listener at
     once and re-initialisation can never double-bind.
   - Theme-editor safe: binds on DOMContentLoaded and shopify:section:load,
     unbinds on shopify:section:unload.

   Note on the mobile focus model: the drawer opens *beneath* a header that
   stays visible and usable, so focus is confined to header + active panel
   rather than the drawer alone. Trapping inside the drawer would strand the
   logo, search, account and cart controls.
   ========================================================================== */

(function () {
  'use strict';

  var HOVER_OPEN_DELAY = 110;
  var HOVER_CLOSE_DELAY = 170;
  var DESKTOP_QUERY = '(min-width: 1100px)';
  var FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  var instances = new WeakMap();

  function visibleFocusable(container) {
    if (!container) return [];
    return Array.prototype.filter.call(container.querySelectorAll(FOCUSABLE), function (el) {
      var rect = el.getBoundingClientRect();
      return (rect.width > 0 || rect.height > 0) && getComputedStyle(el).visibility !== 'hidden';
    });
  }

  function createHeader(root) {
    if (!root || instances.has(root)) return null;

    var controller = new AbortController();
    var signal = controller.signal;
    var desktop = window.matchMedia(DESKTOP_QUERY);

    var openTimer = null;
    var closeTimer = null;
    var lastFocusedBeforeDrawer = null;
    var stateObserver = null;
    var sizeObserver = null;

    var section = root.closest('.shopify-section') || document;
    var drawer = section.querySelector('[data-specgt-drawer]');
    var drawerToggle = root.querySelector('[data-specgt-drawer-toggle]');
    var megaItems = Array.prototype.slice.call(root.querySelectorAll('[data-specgt-mega-item]'));
    var dropdownItems = Array.prototype.slice.call(root.querySelectorAll('[data-specgt-dropdown-item]'));
    var searchToggle = root.querySelector('[data-specgt-search-toggle]');
    var searchPanel = root.querySelector('[data-specgt-search]');
    var searchInput = root.querySelector('[data-specgt-search-input]');
    var sentinel = root.querySelector('[data-specgt-sentinel]');

    var openLabel = drawerToggle ? drawerToggle.getAttribute('aria-label') : '';

    function clearTimers() {
      if (openTimer) { window.clearTimeout(openTimer); openTimer = null; }
      if (closeTimer) { window.clearTimeout(closeTimer); closeTimer = null; }
    }

    /* ---------------------------------------------------------------- mega */

    function setDisclosure(item, open) {
      if (!item) return;
      item.setAttribute('data-open', open ? 'true' : 'false');
      var trigger = item.querySelector('[data-specgt-mega-trigger], [data-specgt-dropdown-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    function syncMegaFlag() {
      var anyOpen = megaItems.some(function (item) {
        return item.getAttribute('data-open') === 'true';
      });
      root.setAttribute('data-any-mega-open', anyOpen ? 'true' : 'false');
    }

    function closeAllMenus(except) {
      clearTimers();
      megaItems.concat(dropdownItems).forEach(function (item) {
        if (item === except) return;
        setDisclosure(item, false);
      });
      syncMegaFlag();
    }

    /* -------------------------------------------------------------- search */

    function setSearch(open) {
      if (!searchPanel) return;
      root.setAttribute('data-search-open', open ? 'true' : 'false');
      searchPanel.setAttribute('aria-hidden', open ? 'false' : 'true');
      if (searchToggle) searchToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open && searchInput) {
        window.requestAnimationFrame(function () { searchInput.focus(); });
      }
    }

    function searchIsOpen() {
      return root.getAttribute('data-search-open') === 'true';
    }

    /* -------------------------------------------------------------- drawer */

    function panels() {
      return drawer ? Array.prototype.slice.call(drawer.querySelectorAll('[data-panel-id]')) : [];
    }

    function activePanel() {
      return drawer ? drawer.querySelector('[data-panel-id][data-active="true"]') : null;
    }

    function showPanel(id, focusFirst) {
      if (!drawer) return;
      panels().forEach(function (panel) {
        var active = panel.getAttribute('data-panel-id') === id;
        panel.setAttribute('data-active', active ? 'true' : 'false');
        panel.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
      var target = drawer.querySelector('[data-panel-id="' + id + '"]');
      if (target) {
        target.scrollTop = 0;
        if (focusFirst) {
          window.requestAnimationFrame(function () {
            var first = visibleFocusable(target)[0];
            if (first) first.focus();
          });
        }
      }
    }

    function drawerIsOpen() {
      return document.body.getAttribute('data-specgt-drawer-open') === 'true';
    }

    function positionDrawer() {
      if (!drawer) return;
      var bottom = Math.round(root.getBoundingClientRect().bottom);
      drawer.style.setProperty('--specgt-drawer-top', Math.max(bottom, 0) + 'px');
    }

    function openDrawer() {
      if (!drawer || drawerIsOpen()) return;
      lastFocusedBeforeDrawer = document.activeElement;
      closeAllMenus();
      setSearch(false);
      positionDrawer();
      document.body.setAttribute('data-specgt-drawer-open', 'true');
      drawer.setAttribute('aria-hidden', 'false');
      if (drawerToggle) {
        drawerToggle.setAttribute('aria-expanded', 'true');
        drawerToggle.setAttribute('aria-label', drawerToggle.getAttribute('data-close-label') || openLabel);
      }
      showPanel('root', false);
    }

    function closeDrawer(restoreFocus) {
      if (!drawer || !drawerIsOpen()) return;
      document.body.removeAttribute('data-specgt-drawer-open');
      drawer.setAttribute('aria-hidden', 'true');
      if (drawerToggle) {
        drawerToggle.setAttribute('aria-expanded', 'false');
        drawerToggle.setAttribute('aria-label', openLabel);
      }
      // Collapse every disclosure so a reopen starts clean.
      Array.prototype.forEach.call(drawer.querySelectorAll('[data-panel-target]'), function (btn) {
        btn.setAttribute('aria-expanded', 'false');
      });
      showPanel('root', false);
      if (restoreFocus !== false && lastFocusedBeforeDrawer && typeof lastFocusedBeforeDrawer.focus === 'function') {
        lastFocusedBeforeDrawer.focus();
      }
      lastFocusedBeforeDrawer = null;
    }

    // Focus stays within the header plus the visible panel, in DOM order.
    function trapFocus(event) {
      if (event.key !== 'Tab' || !drawerIsOpen() || !drawer) return;
      var items = visibleFocusable(root).concat(visibleFocusable(activePanel()));
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /* --------------------------------------------------------------- bind */

    megaItems.forEach(function (item) {
      var trigger = item.querySelector('[data-specgt-mega-trigger]');
      if (!trigger) return;

      item.addEventListener('mouseenter', function () {
        if (!desktop.matches) return;
        clearTimers();
        openTimer = window.setTimeout(function () {
          closeAllMenus(item);
          setDisclosure(item, true);
          syncMegaFlag();
        }, HOVER_OPEN_DELAY);
      }, { signal: signal });

      item.addEventListener('mouseleave', function () {
        if (!desktop.matches) return;
        clearTimers();
        closeTimer = window.setTimeout(function () {
          setDisclosure(item, false);
          syncMegaFlag();
        }, HOVER_CLOSE_DELAY);
      }, { signal: signal });

      trigger.addEventListener('click', function (event) {
        if (!desktop.matches) return;
        event.preventDefault();
        var willOpen = item.getAttribute('data-open') !== 'true';
        closeAllMenus(item);
        setSearch(false);
        setDisclosure(item, willOpen);
        syncMegaFlag();
      }, { signal: signal });

      item.addEventListener('focusout', function (event) {
        if (!desktop.matches) return;
        if (event.relatedTarget && item.contains(event.relatedTarget)) return;
        setDisclosure(item, false);
        syncMegaFlag();
      }, { signal: signal });
    });

    dropdownItems.forEach(function (item) {
      var trigger = item.querySelector('[data-specgt-dropdown-trigger]');
      if (!trigger) return;

      trigger.addEventListener('click', function (event) {
        if (!desktop.matches) return;
        event.preventDefault();
        var willOpen = item.getAttribute('data-open') !== 'true';
        closeAllMenus(item);
        setSearch(false);
        setDisclosure(item, willOpen);
      }, { signal: signal });

      item.addEventListener('focusout', function (event) {
        if (!desktop.matches) return;
        if (event.relatedTarget && item.contains(event.relatedTarget)) return;
        setDisclosure(item, false);
      }, { signal: signal });
    });

    if (searchToggle) {
      searchToggle.addEventListener('click', function (event) {
        event.preventDefault();
        closeAllMenus();
        setSearch(!searchIsOpen());
      }, { signal: signal });
    }

    if (drawerToggle) {
      drawerToggle.addEventListener('click', function (event) {
        event.preventDefault();
        if (drawerIsOpen()) { closeDrawer(); } else { openDrawer(); }
      }, { signal: signal });
    }

    if (drawer) {
      drawer.addEventListener('click', function (event) {
        var drill = event.target.closest('[data-panel-target]');
        if (drill) {
          event.preventDefault();
          drill.setAttribute('aria-expanded', 'true');
          showPanel(drill.getAttribute('data-panel-target'), true);
          return;
        }

        var back = event.target.closest('[data-panel-back]');
        if (back) {
          event.preventDefault();
          var fromPanel = back.closest('[data-panel-id]');
          var parentId = back.getAttribute('data-panel-back');
          showPanel(parentId, false);
          if (fromPanel) {
            var opener = drawer.querySelector('[data-panel-target="' + fromPanel.getAttribute('data-panel-id') + '"]');
            if (opener) {
              opener.setAttribute('aria-expanded', 'false');
              window.requestAnimationFrame(function () { opener.focus(); });
            }
          }
        }
      }, { signal: signal });

      drawer.addEventListener('keydown', trapFocus, { signal: signal });
      root.addEventListener('keydown', trapFocus, { signal: signal });
    }

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      if (drawerIsOpen()) {
        closeDrawer();
        return;
      }
      if (searchIsOpen()) {
        setSearch(false);
        if (searchToggle) searchToggle.focus();
        return;
      }
      var openItem = megaItems.concat(dropdownItems).filter(function (item) {
        return item.getAttribute('data-open') === 'true';
      })[0];
      if (openItem) {
        var trigger = openItem.querySelector('[data-specgt-mega-trigger], [data-specgt-dropdown-trigger]');
        closeAllMenus();
        if (trigger) trigger.focus();
      }
    }, { signal: signal });

    document.addEventListener('click', function (event) {
      if (root.contains(event.target)) return;
      if (drawer && drawer.contains(event.target)) return;
      closeAllMenus();
      setSearch(false);
    }, { signal: signal });

    desktop.addEventListener('change', function () {
      closeAllMenus();
      setSearch(false);
      closeDrawer(false);
    }, { signal: signal });

    /* ------------------------------------------- header state + measurement */

    if (sentinel && 'IntersectionObserver' in window) {
      stateObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          root.setAttribute('data-header-state', entry.isIntersecting ? 'top' : 'scrolled');
        });
      }, { rootMargin: '0px', threshold: 0 });
      stateObserver.observe(sentinel);
    }

    function publishMetrics() {
      document.documentElement.style.setProperty(
        '--specgt-header-height',
        Math.round(root.getBoundingClientRect().height) + 'px'
      );
      if (drawerIsOpen()) positionDrawer();
    }

    publishMetrics();

    if ('ResizeObserver' in window) {
      sizeObserver = new ResizeObserver(publishMetrics);
      sizeObserver.observe(root);
    }

    var instance = {
      destroy: function () {
        clearTimers();
        controller.abort();
        if (stateObserver) stateObserver.disconnect();
        if (sizeObserver) sizeObserver.disconnect();
        if (drawerIsOpen()) closeDrawer(false);
        instances.delete(root);
      }
    };

    instances.set(root, instance);
    return instance;
  }

  function initAll(scope) {
    var container = scope || document;
    if (container.matches && container.matches('[data-specgt-header]')) {
      createHeader(container);
    }
    Array.prototype.forEach.call(
      container.querySelectorAll('[data-specgt-header]'),
      createHeader
    );
  }

  function destroyIn(scope) {
    if (!scope || !scope.querySelectorAll) return;
    Array.prototype.forEach.call(
      scope.querySelectorAll('[data-specgt-header]'),
      function (root) {
        var existing = instances.get(root);
        if (existing) existing.destroy();
      }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    destroyIn(event.target);
  });
})();
